import React from 'react';
import { calculateCurrencies } from '@joindeed/currency';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { AutorenewOutlined, FiberManualRecordOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';

import { primaryBlue, purpleish } from 'utils/color-styles';
import config from 'config';
import { currenciesFormat, CurrencyCode } from 'utils/currencyFormat';
import { selectUser } from 'entities/user/selectors';
import { displayName } from 'utils/formatFullName';
import {
  type Organization,
  type Donation,
  type BaseUser,
  DonationFrequency,
  DonationSchedule,
} from 'generated/graphql';

export type GenericDonation = Pick<
  Donation,
  | 'id'
  | 'nonprofits'
  | 'status'
  | 'total'
  | 'amountCurrencies'
  | 'currency'
  | 'rescheduledFrom'
  | 'rescheduledTo'
> & {
  User?: Pick<BaseUser, 'id' | 'fullName' | 'preferredName' | 'mainPicture'> | null;
  Nonprofits?: Pick<Organization, 'id' | 'name' | 'mainPicture'>[] | null;
  amountCurrencies?: Record<CurrencyCode, number> | null;
  DonationSchedule?: Pick<DonationSchedule, 'frequency'> | null;
};

type Props<D extends GenericDonation> = {
  donations?: D[];
  renderStatus: ({ donation }: { donation: D }) => JSX.Element;
  footer?: JSX.Element;
  loading?: boolean;
};

export const DonationsTable = <D extends GenericDonation>({
  donations,
  renderStatus,
  footer,
  loading,
}: Props<D>) => {
  const user = useSelector(selectUser);
  const Status = renderStatus;
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 4 }}>
      {loading && <LinearProgress />}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell size="medium">Employee</TableCell>
            <TableCell size="medium">Nonprofit</TableCell>
            <TableCell size="medium">Amount</TableCell>
            <TableCell size="medium">Frequency</TableCell>
            <TableCell size="medium" style={{ textAlign: 'center' }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {donations?.map((donation) => {
            const totalCurrencies = calculateCurrencies(
              donation.total,
              donation.currency,
              donation.amountCurrencies
            );
            const nonprofitId = donation.nonprofits?.[0];
            const nonprofit = donation.Nonprofits?.[0];
            if (!nonprofitId || !nonprofit) {
              return null;
            }
            return (
              <TableRow
                key={donation.id}
                hover
                sx={{ '&:hover': { backgroundColor: `${purpleish} !important` } }}
                component={Link}
                to={`/donations/${donation.id}`}
              >
                <TableCell size="medium" style={{ lineHeight: '32px' }}>
                  {donation.User && (
                    <>
                      {donation.User.mainPicture && (
                        <Avatar
                          alt={displayName(donation.User)}
                          src={donation.User.mainPicture.replace('http://', 'https://')}
                          style={{ width: 32, height: 32, float: 'left', marginRight: 10 }}
                        />
                      )}
                      <Link to={`/volunteers/${donation.User.id}`}>
                        {displayName(donation.User)}
                      </Link>
                    </>
                  )}
                </TableCell>
                <TableCell size="medium">
                  {nonprofit.mainPicture && (
                    <Avatar
                      alt={nonprofit.name}
                      src={nonprofit.mainPicture.replace('http://', 'https://')}
                      style={{ width: 32, height: 32, float: 'left', marginRight: 10 }}
                    />
                  )}
                  <Typography
                    style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(`${config.clientUrl}/organization/${nonprofit.id}`, '_blank');
                    }}
                  >
                    {nonprofit.name}
                  </Typography>
                </TableCell>
                <TableCell size="medium">
                  {totalCurrencies &&
                    currenciesFormat(totalCurrencies, user?.displayCurrency || 'USD')}
                </TableCell>
                <TableCell size="medium" style={{ width: '1%' }}>
                  {donation?.DonationSchedule &&
                  donation.DonationSchedule?.frequency !== DonationFrequency.OneTime ? (
                    <Chip
                      label="Recurring"
                      avatar={<AutorenewOutlined style={{ color: '#9264CC' }} />}
                      size="small"
                      style={{ backgroundColor: '#EEE0FF', color: '#9264CC' }}
                    />
                  ) : (
                    <Chip
                      label="One-time"
                      avatar={<FiberManualRecordOutlined style={{ color: '#3781B7' }} />}
                      size="small"
                      style={{ backgroundColor: '#D0EBFE', color: '#3781B7' }}
                    />
                  )}
                </TableCell>
                <TableCell size="medium" style={{ width: '1%' }}>
                  <Status donation={donation} />
                  {donation.rescheduledFrom ? (
                    <Chip
                      label="Rescheduled"
                      avatar={<AutorenewOutlined style={{ color: '#3781B7' }} />}
                      size="small"
                      style={{ backgroundColor: '#D0EBFE', color: '#3781B7', marginTop: '15px' }}
                    />
                  ) : (
                    ''
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        {footer}
      </Table>
    </TableContainer>
  );
};
